import {skipToken} from '@reduxjs/toolkit/query';
import {Parameter} from 'platform/components';
import {useDateTimeFormatter} from 'platform/locale';

import {isNotNil} from 'ramda';

import {useGetCustomerV2Query} from '@dms/api/customer';
import {
  useGetMetadaServiceCaseQuery,
  useGetServiceCaseByServiceOrderIdQuery,
  useGetServiceOrderQuery,
} from '@dms/api/workshop';
import i18n from '@dms/i18n';
import {workshopRoutes} from '@dms/routes';
import {getCustomerName} from '@dms/shared';

import {buildArray, composePath, parseDate} from 'shared/web';

import {PairedRecordView} from './PairedRecordView';
import {resolvePairedRecordCode} from './resolvePairedRecordCode';

export function useServiceOrderPairedRecord(serviceOrderId: string | null): PairedRecordView {
  const {data: serviceCaseByOrder, isLoading: isServiceCaseByOrderLoading} =
    useGetServiceCaseByServiceOrderIdQuery(isNotNil(serviceOrderId) ? {serviceOrderId} : skipToken);
  const serviceCaseId = serviceCaseByOrder?.id ?? null;

  const {data: serviceOrder, isLoading: isServiceOrderLoading} = useGetServiceOrderQuery(
    isNotNil(serviceCaseId) && isNotNil(serviceOrderId)
      ? {serviceCaseId, serviceOrderId}
      : skipToken
  );

  const {data: serviceCase} = useGetMetadaServiceCaseQuery(
    isNotNil(serviceCaseId) ? {serviceCaseId} : skipToken
  );

  // Reflect the whole chain: the service order can't load until its parent service case resolves,
  // so the prerequisite lookup counts as loading too (otherwise the snippet flashes "not found").
  const isLoading = isServiceCaseByOrderLoading || isServiceOrderLoading;

  const customerId =
    serviceOrder?.billingInformation?.customerInformation?.customerId ??
    serviceCase?.customerId ??
    serviceCaseByOrder?.customerId ??
    null;

  const {data: customer} = useGetCustomerV2Query(isNotNil(customerId) ? {customerId} : skipToken);

  const formatDateTime = useDateTimeFormatter();

  const customerName = getCustomerName(customer);
  const completionAtFormatted = serviceOrder?.completionAt
    ? formatDateTime('dateShort', parseDate(serviceOrder.completionAt))
    : null;
  const closedAtFormatted = serviceOrder?.closedAt
    ? formatDateTime('dateShort', parseDate(serviceOrder.closedAt))
    : null;

  const parameters = buildArray<Parameter>()
    .when(isNotNil(customerName), `${i18n.t('entity.customer.labels.customer')}: ${customerName}`)
    .when(isNotNil(serviceOrder?.name), `${i18n.t('general.labels.name')}: ${serviceOrder?.name}`)
    .when(
      isNotNil(completionAtFormatted),
      `${i18n.t('general.labels.completedAt')} ${completionAtFormatted}`
    )
    .when(isNotNil(closedAtFormatted), `${i18n.t('general.labels.closed')} ${closedAtFormatted}`);

  return {
    isLoading,
    code: resolvePairedRecordCode(serviceOrder?.number, isLoading),
    detailPath: isNotNil(serviceCaseId)
      ? composePath(workshopRoutes.serviceCaseDetail, {params: {id: serviceCaseId}})
      : null,
    parameters,
    vehicleIds: isNotNil(serviceCase?.vehicleId) ? [serviceCase.vehicleId] : [],
  };
}
